import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import { libraryService } from '../services/supabase';
import { Library } from '../types/models';
import { FiPlus, FiSearch, FiBook, FiEdit2, FiTrash2, FiCheck, FiX, FiFolder, FiLock, FiUnlock } from 'react-icons/fi';
import { normalizeImageUrl } from '../utils/imageUtils';

const LibraryList: React.FC = () => {
  const [libraries, setLibraries] = useState<Library[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [libraryToDelete, setLibraryToDelete] = useState<Library | null>(null);

  useEffect(() => {
    fetchLibraries();
  }, []);

  const fetchLibraries = async () => {
    try {
      setLoading(true);
      const { data, error } = await libraryService.getLibraries();
      
      if (error) {
        throw new Error(error.message);
      }
      
      setLibraries(data || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar bibliotecas');
      console.error('Erro ao buscar bibliotecas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (library: Library) => {
    setLibraryToDelete(library);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!libraryToDelete) return;
    
    try {
      setLoading(true);
      const { error } = await libraryService.deleteLibrary(libraryToDelete.id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      // Atualiza a lista de bibliotecas após a exclusão
      setLibraries(libraries.filter(library => library.id !== libraryToDelete.id));
      setShowDeleteModal(false);
      setLibraryToDelete(null);
    } catch (err: any) {
      setError(err.message || 'Erro ao excluir biblioteca');
      console.error('Erro ao excluir biblioteca:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setLibraryToDelete(null);
  };

  // Filtra bibliotecas por termo de busca
  const filteredLibraries = libraries.filter(library => 
    library.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
    library.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white font-raleway mb-2">
          Gerenciamento de <span className="text-vermelho-claro">Bibliotecas</span>
        </h1>
        <p className="text-gray-300">
          Organize materiais educacionais em coleções por temas ou públicos-alvo.
        </p>
      </div>

      {/* Barra de ações */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
        <div className="relative w-full md:w-auto md:min-w-[300px]">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FiSearch className="text-gray-500" />
          </div>
          <input
            type="text"
            placeholder="Buscar bibliotecas..."
            className="input pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <Link 
          to="/bibliotecas/nova" 
          className="btn btn-primary py-2 px-4 flex items-center w-full md:w-auto justify-center"
        >
          <FiPlus className="mr-2" />
          Nova Biblioteca
        </Link>
      </div>

      {/* Lista de bibliotecas */}
      {loading && libraries.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-vermelho-claro"></div>
        </div>
      ) : error ? (
        <div className="bg-red-900/30 border border-red-800 rounded-lg p-4 text-center">
          <p className="text-red-200">{error}</p>
          <button 
            onClick={fetchLibraries}
            className="mt-2 text-vermelho-claro hover:underline"
          >
            Tentar novamente
          </button>
        </div>
      ) : filteredLibraries.length === 0 ? (
        <div className="text-center py-12 bg-gray-900/50 rounded-lg">
          <div className="w-16 h-16 bg-gray-800 rounded-full mx-auto flex items-center justify-center mb-4">
            <FiFolder className="text-gray-600" size={24} />
          </div>
          <h3 className="text-xl font-medium text-gray-400 mb-2">
            Nenhuma biblioteca encontrada
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            {libraries.length === 0 
              ? 'Não há bibliotecas cadastradas. Clique em "Nova Biblioteca" para adicionar.'
              : 'Nenhuma biblioteca corresponde aos termos de busca.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredLibraries.map(library => (
            <div 
              key={library.id} 
              className="bg-gray-900 rounded-lg overflow-hidden border border-gray-800 hover:border-gray-700 transition-all hover:shadow-lg hover:shadow-vermelho-escuro/20 hover:-translate-y-1"
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${library.cor || 'bg-vermelho-escuro'}`}>
                      <FiBook className="text-white" size={18} />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-lg font-semibold text-white">{library.nome}</h3>
                      <div className="flex items-center text-xs text-gray-400 mt-1">
                        {library.is_public ? (
                          <><FiUnlock className="mr-1" size={12} /> Pública</>
                        ) : (
                          <><FiLock className="mr-1" size={12} /> Privada</>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-gray-400 text-sm mb-4 line-clamp-2 h-10">
                  {library.descricao}
                </p>
                
                <div className="flex justify-between mt-4">
                  <Link
                    to={`/bibliotecas/${library.id}`}
                    className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center"
                  >
                    <FiEdit2 className="mr-1" size={14} /> Editar
                  </Link>
                  <button
                    onClick={() => handleDeleteClick(library)}
                    className="text-red-400 hover:text-red-300 text-sm flex items-center"
                  >
                    <FiTrash2 className="mr-1" size={14} /> Excluir
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && libraryToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={handleDeleteCancel}></div>
          <div className="relative bg-gray-900 rounded-lg max-w-md w-full mx-auto z-10 overflow-hidden animate-modal-appear">
            <div className="p-6">
              <div className="mb-4 text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-red-900/50 flex items-center justify-center mb-4">
                  <FiTrash2 className="text-red-500" size={24} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Excluir Biblioteca</h3>
                <p className="text-gray-300">
                  Tem certeza que deseja excluir a biblioteca <span className="font-bold text-white">{libraryToDelete.nome}</span>?
                </p>
                <p className="mt-2 text-sm text-red-400">
                  Esta ação não pode ser desfeita e removerá todos os dados associados.
                </p>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleDeleteCancel}
                  className="flex-1 py-2 px-4 bg-gray-800 hover:bg-gray-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiX className="mr-2" /> Cancelar
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 py-2 px-4 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors font-medium flex items-center justify-center"
                >
                  <FiCheck className="mr-2" /> Confirmar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};

export default LibraryList; 